import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import PhoneFrame from '../components/PhoneFrame';
import BottomNav from '../components/BottomNav';
import PostCard from '../components/PostCard';
import { Atom, Star, Settings as SettingsIcon, LogOut, Users, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Broadcasts', 'Media'];

export default function Profile() {
  const { profile, logout, loadMyPosts, friends } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState('Broadcasts');
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadMyPosts().then((rows) => { if (mounted) { setMine(rows); setLoading(false); } });
    return () => { mounted = false; };
  }, [loadMyPosts]);

  const media = mine.filter((p) => p.image);

  return (
    <PhoneFrame withNav>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10">
          <div className="orbit-page-header justify-between">
            <button className="press orbit-icon-button" aria-label="Log out" style={{ color: '#ff9d9d' }} onClick={async () => { await logout(); nav('/'); }}><LogOut /></button>
            <div className="flex items-center gap-2">
              <button className="press orbit-secondary-button !px-3" onClick={() => nav('/friends')}>
                <Users /> <span>{friends.length}</span>
              </button>
              <button className="press orbit-icon-button" aria-label="Customize profile" onClick={() => nav('/profile/edit')}><Pencil /></button>
              <button className="press orbit-icon-button" aria-label="Settings" onClick={() => nav('/settings')}><Atom /></button>
            </div>
          </div>

          {/* Header */}
          <div className="flex flex-col items-center px-6 -mt-2">
            <div className="rounded-full p-[3px] glow-purple" style={{ background: 'linear-gradient(180deg,#8b7ce8,#4b3f8f)' }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="rounded-full object-cover" style={{ width: 128, height: 128, border: '3px solid #0a0b1e' }} />
              ) : (
                <div className="rounded-full flex items-center justify-center font-bold text-4xl" style={{ width: 128, height: 128, border: '3px solid #0a0b1e', background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                  {(profile?.display_name || profile?.username || '?')[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <h1 className="font-display font-extrabold mt-3" style={{ fontSize: 30, color: '#f3ecff' }}>{profile?.display_name}</h1>
            <div className="text-center" style={{ color: '#a89fc9' }}>@{profile?.username}</div>
            {profile?.bio && <div className="text-center mt-2 max-w-md px-4" style={{ color: '#a89fc9' }}>{profile.bio}</div>}
            {!!profile?.tags?.length && (
              <div className="flex flex-wrap justify-center gap-2 mt-3 max-w-md">
                {profile.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ color: '#d9c9ff', background: 'rgba(111,97,198,.2)', border: '1px solid var(--orbit-border)' }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="px-2 mt-5">
            <div className="relative flex items-center">
              <div className="star-line flex-1" />
              <Star size={22} fill="#e7dcff" color="#e7dcff" className="mx-2" />
              <div className="star-line flex-1" />
            </div>
            <div className="flex justify-center gap-10 mt-2">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className="press relative pb-2 font-display"
                  style={{ fontSize: 16, fontWeight: 600, color: tab === t ? '#f3ecff' : '#6f6795' }}>
                  <span className={tab === t ? 'text-glow-pink' : ''}>{t}</span>
                  {tab === t && <span className="absolute left-0 right-0 -bottom-0 h-[3px] rounded-full glow-pink" style={{ background: '#ff8fe4' }} />}
                </button>
              ))}
            </div>
          </div>
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.5),transparent)' }} />

          {tab === 'Broadcasts' && (
            <>
              <button onClick={() => nav('/compose')} className="press orbit-surface-card mx-4 my-4 flex items-center gap-3 text-left">
                <span className="relative">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-11 h-11 rounded-full object-cover" style={{ border: '1px solid rgba(160,140,230,.4)' }} />
                  ) : (
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                      {(profile?.display_name || profile?.username || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="absolute bottom-0 right-0 rounded-full" style={{ width: 11, height: 11, background: '#37e07a', border: '2px solid #0a0b1e' }} />
                </span>
                <div>
                  <div className="font-semibold" style={{ color: '#efeaff' }}>@{profile?.username}</div>
                  <div style={{ color: '#7d769f' }}>Launch a thought</div>
                </div>
              </button>
              {loading && <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Loading…</p>}
              {!loading && mine.length === 0 && (
                <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>No broadcasts yet.</p>
              )}
              {mine.map((p) => <PostCard key={p.id} post={p} showPinned />)}
            </>
          )}

          {tab === 'Media' && (
            <div className="grid grid-cols-2 gap-1 p-1">
              {media.length === 0 && (
                <div className="col-span-2 text-center py-16" style={{ color: '#6f6795' }}>
                  <SettingsIcon size={20} className="mx-auto mb-2 opacity-40" />
                  No media yet.
                </div>
              )}
              {media.map((p) => (
                <div key={p.id} className="overflow-hidden" style={{ borderRadius: 14, aspectRatio: '1' }}>
                  <img src={p.image} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
        <BottomNav />
      </div>
    </PhoneFrame>
  );
}
